<template>
  <v-autocomplete
    ref="autocomplete"
    v-model="selectedId"
    :items="dummyTeams"
    item-text="name"
    item-value="id"
    :filter="customFilter"
    background-color="primary"
    @change="onChange"
    :hide-no-data="!dropdownShown"
    class="team-filter"
    @click:clear="onClear"
    @focus="onFocus"
    @input.native="onInput"
    dense
    filled
    hide-details
    clearable
    clear-icon="highlight_off"
    :placeholder="placeholder"
    append-icon=""
    prepend-inner-icon="search"
  >
    <!-- Superteams are marked with a prefix icon so they stand out from teams in
         the shared results list; teams render with a matching-width spacer so the
         names stay aligned. -->
    <template #item="{ item }">
      <v-list-item-icon class="team-filter__icon">
        <v-icon v-if="item.type === 'superteam'" size="20">$graph2</v-icon>
      </v-list-item-icon>
      <v-list-item-content>
        <v-list-item-title>{{ item.name }}</v-list-item-title>
      </v-list-item-content>
    </template>
    <template #selection="{ item }">
      <v-icon
        v-if="item.type === 'superteam'"
        size="18"
        class="team-filter__sel-icon"
        >$familyHistory</v-icon
      >
      <span class="team-filter__sel-text">{{ item.name }}</span>
    </template>
  </v-autocomplete>
</template>
<script>
import { swrGet } from "../utils/swrCache";
import {
  LEADERBOARD_PAGE_SIZE,
  MERGED_TAB_ITEMS,
  DEFAULT_LEADERBOARD_SORT,
  TEAM_SEARCH_INCLUDE,
} from "../utils/constants";
import { mapActions, mapState } from "vuex";

export default {
  data() {
    return {
      // Search results — a mix of teams and superteams, each carrying a `type`
      // discriminator.
      dummyTeams: [],
      selectedId: null,
      userInput: null,
    };
  },
  computed: {
    ...mapState({
      storeTeam: "team",
      storeTeamName: "teamName",
      storeTeamSlug: "teamSlug",
      storeSuperteam: "superteam",
      storeSuperteamName: "superteamName",
      storeSuperteamSlug: "superteamSlug",
      // Active filter scope — mirrored into the empty-field suggestion request so
      // its SWR cache key matches the leaderboard's teams page (an instant hit).
      startDay: "startDay",
      endDay: "endDay",
      country: "country",
    }),
    dropdownShown() {
      return this.userInput;
    },
    // Drop the placeholder once a selection fills the field: the #selection slot
    // renders on top of it, leaving stray text behind the name (visible in dark
    // mode, hidden only by a matching background in light mode).
    placeholder() {
      return this.selectedId ? undefined : "Search a Team or Superteam...";
    },
    // The scope this field reflects: superteam takes priority over a drilled
    // child team, so the field always names the top-level selection (the drilled
    // team is surfaced by the leaderboard breadcrumb + a stats chip instead).
    topScope() {
      if (this.storeSuperteam) {
        return {
          id: this.storeSuperteam,
          name: this.storeSuperteamName,
          slug: this.storeSuperteamSlug,
          type: "superteam",
        };
      }
      if (this.storeTeam) {
        return {
          id: this.storeTeam,
          name: this.storeTeamName,
          slug: this.storeTeamSlug,
          type: "team",
        };
      }
      return { id: null, name: null, slug: null, type: "team" };
    },
  },
  watch: {
    // Reflect the scope when it's set/changed elsewhere (leaderboard row click,
    // filter-chip clear) so the field shows the right name and can be cleared
    // here. topScope is a fresh object each time, so this fires on any change.
    topScope: {
      immediate: true,
      handler({ id, name, slug, type }) {
        if (id) {
          if (!this.dummyTeams.some((t) => t.id === id)) {
            // Carry the slug onto the synthetic row too: re-picking this entry
            // from the menu goes through onChange, which reads it from here.
            this.dummyTeams = [{ id, name, slug, type }, ...this.dummyTeams];
          }
          this.selectedId = id;
        } else {
          this.selectedId = null;
        }
      },
    },
  },
  methods: {
    ...mapActions(["selectTeam", "selectSuperteam", "clearSuperteam"]),
    customFilter() {
      return true;
    },
    // Params for the empty-field suggestion request. Mirrors
    // Leaderboard.buildParams("teams", 0) exactly (same keys, same order-agnostic
    // values) so the SWR cache key matches the teams page the leaderboard already
    // loads/prefetches — making loadSuggestions an instant cache hit in the common
    // case rather than a fresh round trip. team/superteam are null whenever the
    // field is empty, so they never enter the key.
    suggestionParams() {
      const params = {
        // Same value the leaderboard uses, or the cache key stops matching. The
        // suggestions therefore include superteams, which onChange routes by
        // `type`.
        tabItems: MERGED_TAB_ITEMS,
        offset: 0,
        limit: LEADERBOARD_PAGE_SIZE,
        country: this.country,
        // The leaderboard's own default sort, for the same reason: a panel that
        // has just loaded requests this order, and only that order shares this
        // cache key. Sorting the panel by another column doesn't re-sort the
        // suggestions — they are the top teams by items picked either way.
        sortBy: DEFAULT_LEADERBOARD_SORT,
        // No user_id, matching buildParams: the ranking isn't narrowed to a
        // selected strider, so neither are the suggestions drawn from it.
      };
      if (this.startDay && this.endDay) {
        params.start_date = this.startDay;
        params.end_date = this.endDay;
      }
      return params;
    },
    // Suggestions shown when the field is focused while empty: the top teams by
    // items picked for the active scope, taken from the leaderboard's ranked
    // `teams` page. Strides carry no team id, so these are filter-scoped (not the
    // pins literally in the map viewport) — see
    // docs/todos/backend-changes-for-dashboard-revamp.md §7.
    async loadSuggestions() {
      try {
        const { cached, fresh } = swrGet("/dashboards/leaderboard", {
          params: this.suggestionParams(),
        });
        // Rank order comes from the server. A row's own `type` wins (the merged
        // ranking states it); otherwise tag as a team so onChange routes it to
        // selectTeam.
        const toItems = (teams) =>
          (teams || []).map((t) => ({
            id: t.id,
            name: t.name,
            slug: t.slug || null,
            type: t.type || "team",
          }));
        if (cached) this.dummyTeams = toItems(cached.teams);
        const data = await fresh;
        // Apply only if the user hasn't started typing a search meanwhile.
        if (!this.userInput) this.dummyTeams = toItems(data.teams);
      } catch (error) {
        console.log(error);
      }
    },
    // Empty field, nothing selected: offer suggestions before the menu renders so
    // the always-true customFilter shows them all.
    onFocus() {
      if (this.selectedId || this.userInput) return;
      this.loadSuggestions();
    },
    async loadTeams(searchText) {
      try {
        // params (not string interpolation) so axios URL-encodes the search
        // text — "&", "#" etc. typed into the field would otherwise break the
        // query string. Served stale-while-revalidate: retyping a previous
        // query fills the dropdown instantly from cache.
        //
        // `include` opts into superteam rows; without it the search returns teams
        // only. Rows are routed by `type` in onChange — that branch is what makes
        // sending the param safe.
        const { cached, fresh } = swrGet("/dashboards/teams", {
          params: { search: searchText, include: TEAM_SEARCH_INCLUDE },
        });
        if (cached) this.dummyTeams = cached.teams;
        const data = await fresh;
        // Apply only if the user hasn't typed something newer meanwhile.
        if (this.userInput === searchText) this.dummyTeams = data.teams;
      } catch (error) {
        console.log(error);
      }
    },
    // Route the selection by type: superteams set the parent scope, teams the
    // (standalone) team scope. Either composite action clears any active
    // individual filter (team/superteam and individual are mutually exclusive).
    // A falsy id means the field was cleared — handled by onClear, so ignore it
    // here to avoid a double dispatch.
    onChange(id) {
      if (!id) return;
      const selected = this.dummyTeams.find((t) => t.id === id);
      // The row's `slug` (when the backend returned one) is what lets the URL be
      // the human-readable path rather than ?team=<id> — see utils/urlFilters.js.
      if (selected && selected.type === "superteam") {
        this.selectSuperteam({ id, name: selected.name, slug: selected.slug });
      } else {
        this.selectTeam({
          id,
          name: selected ? selected.name : null,
          slug: selected ? selected.slug : null,
        });
      }
    },
    onClear() {
      this.userInput = null;
      // Clear the whole scope (superteam + any drilled team, or a standalone
      // team) in one go — clearSuperteam handles both cases.
      this.clearSuperteam();
      // The field is now empty and unselected (the topScope watcher nulls
      // selectedId), so show the suggestions rather than an empty menu.
      this.loadSuggestions();
      // Clearing can happen while the input isn't focused (clicking ✕ on a
      // filled field, menu closed). Keep the invariant that a focused field
      // always shows the list: focus the input and open the menu so the
      // suggestions become visible.
      this.$nextTick(() => {
        const ac = this.$refs.autocomplete;
        if (!ac) return;
        ac.focus();
        ac.activateMenu();
      });
    },
    onInput(inputEvent) {
      const userInput = inputEvent.target.value;
      this.userInput = userInput;

      if (userInput) {
        this.loadTeams(userInput);
      } else {
        // Back to empty — restore the suggestions rather than blanking the menu.
        this.loadSuggestions();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.team-filter ::v-deep .v-input__slot .v-select__slot input {
  font-weight: 800;
}
.team-filter ::v-deep .v-icon {
  color: rgba(0, 0, 0, 0.54) !important;
}
// The search box is filled with the brand lime (background-color="primary") in
// both themes, so its foreground must stay dark in dark mode too — otherwise the
// themed body text/placeholder flip to light and vanish on the lime. The .v-icon
// rule above is theme-agnostic, so the search/clear icons already stay dark.
.theme--dark .team-filter ::v-deep input,
.theme--dark .team-filter__sel-text {
  color: rgba(0, 0, 0, 0.87) !important;
}
.theme--dark .team-filter ::v-deep input::placeholder {
  color: rgba(0, 0, 0, 0.6) !important;
}
.team-filter ::v-deep .v-input__slot .v-select__slot input {
  caret-color: auto;
}
// Keep the dropdown-row prefix-icon column narrow and its width constant whether
// or not a row has an icon, so team and superteam names line up.
.team-filter__icon {
  min-width: 24px;
  width: 24px;
  margin-left: -2px;
  margin-right: 2px !important;
  align-self: center;
}
.team-filter__sel-icon {
  margin-right: 4px;
}
.team-filter__sel-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
